/**
 * 物件住所のパース (R1: 指定は自治体が町丁目単位で決める)。
 *
 * ナレッジレコードとの照合キー (区+町+丁目) を住所文字列から取り出す。
 * templates.ts の extractWard は横浜市を「市」レベルで返すため再利用しない —
 * 横浜市中区 と 西区 の区別 (どちらにも実績がある) にはここの複合キーが必要。
 *
 * 制約 (仕様):
 *   - 丁目を持たない町 (例: 金港町1-4) では先頭の街区番号を chome と誤認しうる。
 *     照合側 (knowledge.ts) はレコードが chome を持つ時だけ chome を比較する
 *     契約なので、丁目なし町のレコード (chome 未設定) では実害がない。
 *   - 札幌の条丁目 (南10条西3-1-1) は「南10条西3」を町として扱い chome は取らない。
 */

export interface ParsedAddress {
  pref?: string;
  /** 政令指定都市名 (横浜市 等)。特別区・一般市は undefined。 */
  city?: string;
  /** 区 (特別区/行政区) または市町村。最小行政単位。 */
  ward?: string;
  /** 町名 (丁目・街区を除く)。 */
  town?: string;
  /** 丁目 (取れた場合のみ)。 */
  chome?: number;
  /** city+ward の複合キー (例: 横浜市西区)。ward が無ければ undefined。 */
  wardKey?: string;
}

const KANJI_DIGITS: Record<string, number> = {
  一: 1, 二: 2, 三: 3, 四: 4, 五: 5, 六: 6, 七: 7, 八: 8, 九: 9,
};

/** 漢数字 (一〜十九) を数値へ。対象外は null。 */
export function kanjiToNumber(s: string): number | null {
  if (!s) return null;
  if (s === '十') return 10;
  const juIdx = s.indexOf('十');
  if (juIdx === -1) {
    return s.length === 1 ? (KANJI_DIGITS[s] ?? null) : null;
  }
  // 十X (11〜19) のみ対応 (丁目に二十以上は実在しない)
  if (juIdx !== 0 || s.length > 2) return null;
  const ones = KANJI_DIGITS[s.slice(1)];
  return ones ? 10 + ones : null;
}

/** 〒NNN-NNNN・先頭の「日本、」等の前置きを除去し NFKC 正規化する。 */
function preprocess(address: string): string {
  return address
    .normalize('NFKC')
    // ダッシュ類 (U+2212 − や長音符など NFKC で半角化されないもの) を数字直後に限り '-' へ
    .replace(/(\d)[ー−–—―‐]/g, '$1-')
    .replace(/^日本[、,\s]*/, '')
    .replace(/〒?\d{3}-\d{4}\s*/, '')
    .trim();
}

/**
 * 住所文字列を 都道府県 / (政令市) / 区市町村 / 町 / 丁目 に分解する。
 * どの段も取れなければ該当フィールドは undefined (throw しない)。
 */
export function parsePropertyAddress(address: string): ParsedAddress {
  const out: ParsedAddress = {};
  let rest = preprocess(address);
  if (!rest) return out;

  const prefMatch = rest.match(/^(.{2,3}?[都道府県])/);
  if (prefMatch) {
    out.pref = prefMatch[1];
    rest = rest.slice(prefMatch[1]!.length);
  }

  // 政令市 + 行政区 (横浜市西区 / 大阪市中央区 ...)
  const seireiMatch = rest.match(/^(.+?市)(.+?区)/);
  if (seireiMatch) {
    out.city = seireiMatch[1];
    out.ward = seireiMatch[2];
    rest = rest.slice(seireiMatch[1]!.length + seireiMatch[2]!.length);
  } else {
    // 郡部 (○○郡△△町) → 町村を ward に
    const gunMatch = rest.match(/^(.+?郡)(.+?[町村])/);
    if (gunMatch) {
      out.ward = gunMatch[2];
      rest = rest.slice(gunMatch[1]!.length + gunMatch[2]!.length);
    } else {
      const wardMatch = rest.match(/^(.+?[区市町村])/);
      if (wardMatch) {
        out.ward = wardMatch[1];
        rest = rest.slice(wardMatch[1]!.length);
      }
    }
  }

  if (out.ward) out.wardKey = `${out.city ?? ''}${out.ward}`;
  if (!rest) return out;

  // 札幌等の条丁目: 「南10条西3」を町として扱う (chome なし)
  const joMatch = rest.match(/^(.+?\d+条(?:東|西|南|北)?\d*)/);
  if (joMatch) {
    out.town = joMatch[1];
    return out;
  }

  // 漢数字丁目: 本郷一丁目
  const kanjiMatch = rest.match(/^(.+?)([一二三四五六七八九十]{1,2})丁目/);
  if (kanjiMatch) {
    const n = kanjiToNumber(kanjiMatch[2]!);
    if (n) {
      out.town = kanjiMatch[1];
      out.chome = n;
      return out;
    }
  }

  // 算用数字: 銀座4丁目 / 銀座4-5-6 / 丸の内1-9-2
  const arabicMatch = rest.match(/^([^\d\s]+?)(\d+)(?:丁目|番|-|\s|$)/);
  if (arabicMatch) {
    out.town = arabicMatch[1];
    out.chome = Number(arabicMatch[2]);
    return out;
  }

  // 数字なし (町名のみ)
  const townMatch = rest.match(/^([^\d\s]+)/);
  if (townMatch) out.town = townMatch[1];
  return out;
}

/** 東京23区 (特別区) の物件か。municipal-rules の tokyoSpecialWard 判定に使う。 */
export function isTokyoSpecialWard(parsed: ParsedAddress): boolean {
  return parsed.pref === '東京都' && !parsed.city && !!parsed.ward?.endsWith('区');
}
